import { useId, useRef, useState } from "react";
import { mergeClasses } from "@fluentui/react-components";
import { Subtract16Regular, Add16Regular } from "@fluentui/react-icons";
import { SectionNote } from "../SectionNote/SectionNote";
import { useStyles } from "./SectionHeader.styles";
import type { ISectionHeaderProps } from "./SectionHeader.types";

export const SectionHeader = ({
  title,
  note,
  defaultExpanded = true,
  children,
}: ISectionHeaderProps): JSX.Element => {
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const noteId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const handleToggle = (): void => {
    setIsExpanded((previous) => {
      const next = !previous;
      if (!next && contentRef.current?.contains(document.activeElement)) {
        toggleRef.current?.focus();
      }
      return next;
    });
  };

  return (
    <div className={styles.header}>
      <div className={styles.titleRow}>
        <span className={styles.title}>{title}</span>
        <button
          ref={toggleRef}
          type="button"
          className={styles.toggle}
          aria-expanded={isExpanded}
          aria-controls={noteId}
          aria-label={`${isExpanded ? "Hide" : "Show"} ${title} section`}
          onClick={handleToggle}
        >
          {isExpanded ? (
            <Subtract16Regular aria-hidden="true" />
          ) : (
            <Add16Regular aria-hidden="true" />
          )}
        </button>
      </div>
      <div
        id={noteId}
        ref={contentRef}
        className={mergeClasses(
          styles.content,
          !isExpanded && styles.contentHidden,
        )}
        hidden={!isExpanded}
      >
        {note && <SectionNote>{note}</SectionNote>}
        {children}
      </div>
    </div>
  );
};
